<!-- WhatsAppCSVCampaignDialog.vue -->
<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';
import WhatsAppCSVCampaignForm from './WhatsAppCSVCampaignForm.vue';

const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();

const isCreating = computed(
  () => store.state.campaignsCSVWhatsApp.uiFlags.isCreating
);
const isPreviewing = computed(
  () => store.state.campaignsWhatsApp.uiFlags.isPreviewing
);
const isBusy = computed(() => isCreating.value || isPreviewing.value);

const overlayTitle = computed(() =>
  isPreviewing.value
    ? t('CAMPAIGN.CSV.WHATSAPP.CREATE.FORM.UPLOADING.PREVIEW_TITLE')
    : t('CAMPAIGN.CSV.WHATSAPP.CREATE.FORM.UPLOADING.TITLE')
);

/* ───────── crear campaña ───────── */
const createCampaign = async fd => {
  try {
    await store.dispatch('campaignsCSVWhatsApp/create', fd);
    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });
    useAlert(t('CAMPAIGN.CSV.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (e) {
    useAlert(
      e?.response?.message ??
        t('CAMPAIGN.CSV.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE')
    );
  }
};

const handlePreview = async previewData => {
  try {
    await store.dispatch('campaignsWhatsApp/preview', previewData);
    useAlert(
      t('CAMPAIGN.CSV.WHATSAPP.CREATE.FORM.PREVIEW_SECTION.SUCCESS_MESSAGE')
    );
  } catch (e) {
    useAlert(
      e?.message ||
        t('CAMPAIGN.CSV.WHATSAPP.CREATE.FORM.PREVIEW_SECTION.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <!-- Overlay -->
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    @click.self="!isBusy && emit('close')"
  >
    <!-- Diálogo -->
    <section
      class="relative w-full max-w-[70rem] max-h-[90vh] bg-n-alpha-3 backdrop-blur-[80px] rounded-2xl border border-n-slate-6 dark:border-slate-800 shadow-xl flex flex-col"
    >
      <!-- Upload / preview overlay -->
      <div
        v-if="isBusy"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 rounded-2xl bg-n-alpha-3 backdrop-blur-sm"
      >
        <div
          class="h-10 w-10 animate-spin rounded-full border-4 border-n-slate-7 border-t-n-brand"
        />
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ overlayTitle }}
        </h3>
        <p class="max-w-sm text-center text-sm text-n-slate-11">
          {{ t('CAMPAIGN.CSV.WHATSAPP.CREATE.FORM.UPLOADING.DESCRIPTION') }}
        </p>
        <p class="text-xs font-medium text-n-amber-11">
          {{ t('CAMPAIGN.CSV.WHATSAPP.CREATE.FORM.UPLOADING.DO_NOT_CLOSE') }}
        </p>
      </div>

      <!-- Header -->
      <header
        class="flex items-center justify-between px-6 py-4 border-b border-n-slate-6 dark:border-slate-800"
      >
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('CAMPAIGN.CSV.WHATSAPP.CREATE.TITLE') }}
        </h2>
      </header>

      <!-- Body -->
      <main class="flex-1 overflow-y-auto">
        <WhatsAppCSVCampaignForm
          @submit="createCampaign"
          @cancel="emit('close')"
          @preview="handlePreview"
        />
      </main>
    </section>
  </div>
</template>
